import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, ArrowRight, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "../landing/Navbar";
import { Footer } from "../landing/Footer";
import { SubmissionFormDialog } from "../landing/SubmissionFormDialog";
import { ContactDialog } from "../landing/ContactDialog";
import { SEOHead } from "./SEOHead";
import { MobileStickyBar } from "./MobileStickyBar";

/**
 * Shared layout for SEO landing pages.
 * - Manages document head (title/desc/canonical/FAQ schema)
 * - Renders Navbar + hero + slotted content + Footer
 * - Provides form and contact dialogs
 * - Adds mobile sticky CTA
 */
export const SEOPageLayout = ({
  title,
  description,
  canonical,
  h1,
  intro,
  primaryKeyword,
  breadcrumbLabel,
  faqs,
  children,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const openForm = () => setIsFormOpen(true);
  const openContact = () => setIsContactOpen(true);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead title={title} description={description} canonical={canonical} faqs={faqs} />
      <Navbar onOpenForm={openForm} onOpenContact={openContact} />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-[#F5F5F0]" data-testid="seo-hero">
        <div className="container-custom max-w-4xl">
          {/* Breadcrumb */}
          <nav className="text-sm text-[#4B5563] mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#0F5132]">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-[#1F2937] font-medium">{breadcrumbLabel}</span>
          </nav>

          {primaryKeyword && (
            <div className="inline-flex items-center gap-2 bg-[#0F5132]/10 text-[#0F5132] px-3 py-1.5 rounded-full text-xs font-semibold mb-4 uppercase tracking-wide">
              <Package className="w-3.5 h-3.5" />
              {primaryKeyword}
            </div>
          )}

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#1F2937] mb-6" style={{ fontFamily: "Manrope" }} data-testid="seo-page-h1">
            {h1}
          </h1>

          {intro && (
            <p className="text-lg md:text-xl text-[#4B5563] leading-relaxed max-w-3xl mb-8">
              {intro}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <a href="tel:+19049559646" data-testid="hero-call-link">
              <Button className="bg-[#0F5132] hover:bg-[#0B3D25] text-white rounded-full px-6 py-5 text-base font-semibold shadow-md w-full sm:w-auto">
                <Phone className="w-4 h-4 mr-2" />
                Call (904) 955-9646
              </Button>
            </a>
            <Button onClick={openForm} className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-full px-6 py-5 text-base font-semibold shadow-md" data-testid="hero-get-started-btn">
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="sms:+19049559646" data-testid="hero-sms-link">
              <Button variant="outline" className="rounded-full px-6 py-5 text-base font-medium border-2 border-[#EA580C] text-[#EA580C] hover:bg-[#EA580C]/5 w-full sm:w-auto">
                <MessageCircle className="w-4 h-4 mr-2" />
                Text a Photo
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Page body */}
      <main className="pb-24 md:pb-16">
        <div className="container-custom max-w-4xl py-12 md:py-16 space-y-14 md:space-y-16">
          {typeof children === "function" ? children({ openForm, openContact }) : children}
        </div>
      </main>

      <Footer />
      <MobileStickyBar onGetStarted={openForm} />

      <SubmissionFormDialog open={isFormOpen} onOpenChange={setIsFormOpen} />
      <ContactDialog open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
};

/** Standard bottom-of-section CTA used across SEO pages. */
export const InlineCTA = ({ onGetStarted, headline, sub }) => (
  <div className="bg-[#0F5132] rounded-2xl p-8 md:p-10 text-white text-center" data-testid="inline-cta">
    <h3 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: "Manrope" }}>
      {headline}
    </h3>
    {sub && <p className="text-white/90 mb-6 max-w-xl mx-auto">{sub}</p>}
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <a href="tel:+19049559646">
        <Button className="bg-white text-[#0F5132] hover:bg-white/90 rounded-full px-6 py-5 text-base font-semibold w-full sm:w-auto">
          <Phone className="w-4 h-4 mr-2" />
          Call Now: (904) 955-9646
        </Button>
      </a>
      <Button onClick={onGetStarted} className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-full px-6 py-5 text-base font-semibold">
        Contact Us Today
      </Button>
    </div>
  </div>
);

/** Renders an FAQ list matching what SEOHead injects into schema. */
export const FAQList = ({ faqs }) => (
  <div className="space-y-4" data-testid="faq-list">
    {faqs.map((faq) => (
      <div key={faq.q} className="bg-[#F5F5F0] rounded-xl p-5 md:p-6">
        <h3 className="font-semibold text-lg text-[#1F2937] mb-2" style={{ fontFamily: "Manrope" }}>
          {faq.q}
        </h3>
        <p className="text-[#4B5563] leading-relaxed">{faq.a}</p>
      </div>
    ))}
  </div>
);
